import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import {
  ItemCard,
  ItemCardBody,
  ItemCardDivider,
  ItemCardFooter,
  ItemCardMeta,
  ItemCardSection,
  ItemCardSummary,
} from '@sola/ark-core/molecules/item-card/item-card.tsx';
import '@sola/locales/formatters.ts';
import { GraphQLPledge } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { PledgeIcon } from '../../atoms/pledge-icon/pledge-icon.tsx';
import { CommitmentDetailsSheet } from '../../organisms/commitments-details-sheet/commitment-details-sheet.tsx';
import { formatPledgeFooter, formatPledgeName } from '../../utils/pledges.ts';

export type PledgeCardProps = {
  pledge: GraphQLPledge;
  isActive?: boolean;
  onActiveChange?: (open: boolean, commitmentId: string) => void;
};

const useComponentData = (args: PledgeCardProps & { t: TFunction }) => {
  const { pledge, t } = args;

  return {
    ...(pledge.pledgeName && {
      title: formatPledgeName(pledge.pledgeName, t),
    }),
    label: t('my:commitments.pledges.subTitle'),
    rate: t('core:numbers.currencyCustom', {
      amount: pledge.totalPledgeAmount,
    }),
    footer: formatPledgeFooter(t, pledge.nextInstallmentDate),
  };
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const PledgeCard: React.FC<PledgeCardProps> = (props) => {
  const { t } = useTranslation(['my', 'core']);

  const cd = useComponentData({ t, ...props });
  const { pledge, isActive, onActiveChange } = props;

  return (
    <ItemCard>
      <ItemCardSection>
        <ItemCardBody>
          <ItemCardSummary
            icon={<PledgeIcon {...pledge} />}
            title={cd.title}
            subTitle={cd.label}
          />
          <ItemCardMeta text={cd.rate} />
        </ItemCardBody>
      </ItemCardSection>

      {/* Footer */}
      <ItemCardDivider />
      <ItemCardFooter justify="between">
        <span className="label-moderate label-neutral-mild label-sm md:label-md">
          {cd.footer}
        </span>

        <CommitmentDetailsSheet
          pledge={pledge}
          isOpen={isActive}
          onOpenChange={onActiveChange}
          openTrigger={
            <div className="ml-auto">
              <LinkStyle
                noUnderline
                noPadding
                inline
                variant="primary"
                size="lg"
              >
                <button type="button" className="CommitmentCard__trigger">
                  {t('my:commitments.detailsSheet.triggerLabel')}
                </button>
              </LinkStyle>
            </div>
          }
        />
      </ItemCardFooter>
    </ItemCard>
  );
};
